// Taste-signal exclusion core (Feature 73).
//
// The app keeps two different "don't show this again" vocabularies, and mixing
// them up is the classic failure mode this module exists to prevent:
//
//   - watchedKeys      — effective watched state. Used by GENERAL discovery
//     rails (Trending / New Releases / genre shelves). A title lands here via
//     an explicit "Mark watched" override OR an implicit ≥95%-played signal.
//     Rated-but-unwatched titles do NOT belong here: a 👍 on something you
//     haven't finished shouldn't make it vanish from Trending.
//   - seenForRecsKeys  — effective watched ∪ EVERY rated title (any rating,
//     including 👎). Used by the RECOMMENDATION rails ("We think you'll like",
//     "AI picks for you", Because You Watched output): a title the user has
//     rated must never be recommended again, whatever the rating was.
//
// Explicit overrides win in BOTH directions: state='watched' marks a title
// watched even with no playback signal, and state='unwatched' VETOES an
// implicit ≥95% entry for the same key (the "un-hide" path from spec 73a).
//
// Keys use the `${media_type}-${id}` vocabulary of tmdb.js#normalizeItem rows
// (dash separator — distinct from continue-watching.js's `type:id` dedupe key).
//
// This module is the pure, Vue-free core — node --test'able.

/**
 * Build the canonical exclusion-set key for a TMDB identity, or null when the
 * identity is unknown/junk (unknown media type, missing id). Null keys are
 * never added to a set, so junk rows can't poison the exclusion vocabulary.
 */
export function tasteKey(mediaType, id) {
  const type =
    mediaType === "movie" ? "movie" : mediaType === "tv" ? "tv" : null;
  if (!type) return null;
  if (id == null || id === "") return null;
  const idStr = String(id);
  if (!/^\d+$/.test(idStr)) return null;
  return `${type}-${idStr}`;
}

function rowKey(row) {
  if (!row || typeof row !== "object") return null;
  return tasteKey(row.mediaType, row.tmdbId);
}

/**
 * Derive the two exclusion sets from the household's taste signals.
 *
 * @param {object} [signals]
 * @param {Array<{mediaType:string,tmdbId:number,rating:number}>} [signals.ratings]
 *   Explicit ratings (-1 👎 / 1 👍 / 2 ❤️) — the /api/ratings rows.
 * @param {Array<{mediaType:string,tmdbId:number,state:'watched'|'unwatched'}>} [signals.watched]
 *   Explicit watched OVERRIDES (both directions) — the /api/watched rows.
 * @param {Array<{mediaType:string,tmdbId:number}>} [signals.implicitWatched]
 *   Implicit ≥95%-played signals that carry a TMDB id.
 * @returns {{watchedKeys: Set<string>, seenForRecsKeys: Set<string>}}
 */
export function buildExclusionSets({ ratings, watched, implicitWatched } = {}) {
  // Explicit overrides, keyed. Later rows win a (shouldn't-happen) duplicate.
  const overrides = new Map();
  if (Array.isArray(watched)) {
    for (const row of watched) {
      const key = rowKey(row);
      if (!key) continue;
      const state = row.state;
      if (state !== "watched" && state !== "unwatched") continue;
      overrides.set(key, state);
    }
  }

  const watchedKeys = new Set();

  // Implicit ≥95% signals count as watched UNLESS an explicit 'unwatched'
  // override vetoes them (spec 73a: explicit mark wins, both directions).
  if (Array.isArray(implicitWatched)) {
    for (const row of implicitWatched) {
      const key = rowKey(row);
      if (!key) continue;
      if (overrides.get(key) === "unwatched") continue;
      watchedKeys.add(key);
    }
  }

  // Explicit 'watched' overrides always count — no playback signal needed.
  for (const [key, state] of overrides) {
    if (state === "watched") watchedKeys.add(key);
  }

  // Recommendation exclusion = effective watched ∪ ALL rated keys. Any rating
  // value qualifies: a 👎 title must never be recommended, and a 👍/❤️ title
  // is already a known quantity. (Rated keys are NOT added to watchedKeys —
  // general discovery rails only hide what was actually seen.)
  const seenForRecsKeys = new Set(watchedKeys);
  if (Array.isArray(ratings)) {
    for (const row of ratings) {
      const key = rowKey(row);
      if (!key) continue;
      if (!Number.isFinite(Number(row.rating))) continue;
      seenForRecsKeys.add(key);
    }
  }

  return { watchedKeys, seenForRecsKeys };
}

/**
 * Count POSITIVE ratings (👍 = 1, ❤️ = 2). 👎 rows don't count toward the
 * "enough signal to recommend from" threshold — you can't seed
 * recommendations off dislikes alone. Junk rows are ignored.
 */
export function countPositiveRatings(ratings) {
  if (!Array.isArray(ratings)) return 0;
  let n = 0;
  for (const row of ratings) {
    if (!row || typeof row !== "object") continue;
    const rating = Number(row.rating);
    if (Number.isFinite(rating) && rating >= 1) n += 1;
  }
  return n;
}

/**
 * Gate for the recommendation surfaces: does the household have at least
 * `min` positive (👍/❤️) ratings? Mirrors the server-side <3-ratings guard
 * on /api/suggest/for-you.
 */
export function hasEnoughRatings(ratings, min = 3) {
  return countPositiveRatings(ratings) >= min;
}
